// src/pages/SharedCollections.tsx
import React, { useState, useEffect } from 'react';
import {
   Box,
   Typography,
   Card,
   CardMedia,
   CardContent,
   CardActions,
   Button,
   Grid,
   Chip,
   Avatar,
   AvatarGroup,
   IconButton,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   CircularProgress,
   Alert,
   Tooltip,
   Menu,
   MenuItem,
   TextField,
   FormControlLabel,
   Checkbox,
   Divider,
   Snackbar,
} from '@mui/material';
import {
   Visibility as VisibilityIcon,
   Delete as DeleteIcon,
   MoreVert as MoreVertIcon,
   People as PeopleIcon,
   PhotoLibrary as PhotoLibraryIcon,
   Link as LinkIcon,
   Analytics as AnalyticsIcon,
   ViewModule as ViewModuleIcon,
   PersonAdd as PersonAddIcon,
   Edit as EditIcon,
   Save as SaveIcon,
   Cancel as CancelIcon,
   Favorite as FavoriteIcon,
   TrendingUp as TrendingUpIcon,
   Share as ShareIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useUser } from '../contexts/UserContext';
import SharedCollectionService from '../services/sharedCollectionService';
import { SharedCollection } from '../types/SharedCollection';
import { getAuthHeaders } from '../utils/auth';
import { getCombinedViewCount } from '../utils/sharedCollectionPublicStats';

function formatTrafficSourceLabel(key: string): string {
   if (key === 'direct') return 'Direct / untracked';
   if (!key) return 'Unknown';
   return key.charAt(0).toUpperCase() + key.slice(1);
}

interface CollectionAnalytics {
   viewCount: number;
   trafficBySource?: Record<string, number>;
   recipientStats: {
      total: number;
      viewed: number;
      pending: number;
   };
   topViewers: Array<{
      email: string;
      displayName?: string;
      photoURL?: string;
      viewedAt: Date;
      viewCount: number;
   }>;
   likesAnalytics?: {
      totalLikes: number;
      mostLikedPhotos: Array<{
         id: string;
         title: string;
         url: string;
         thumbnailUrl: string;
         contextualLikes: number;
         totalLikes: number;
         uploadedBy: string;
         uploadedAt: Date;
      }>;
      photoCount: number;
      averageLikesPerPhoto: number;
      trafficBySource?: Record<string, number>;
      linkOpenCount?: number;
      emailViewCount?: number;
      totalCombinedViews?: number;
   };
}

const SharedCollections: React.FC = () => {
   const { currentUser } = useUser();
   const [collections, setCollections] = useState<SharedCollection[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [selectedCollection, setSelectedCollection] = useState<SharedCollection | null>(null);
   const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
   const [analytics, setAnalytics] = useState<CollectionAnalytics | null>(null);
   const [analyticsLoading, setAnalyticsLoading] = useState(false);
   const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [selectedCollectionForMenu, setSelectedCollectionForMenu] =
      useState<SharedCollection | null>(null);
   const [shareDialogOpen, setShareDialogOpen] = useState(false);
   const [collectionToShare, setCollectionToShare] = useState<SharedCollection | null>(null);
   const [shareEmails, setShareEmails] = useState('');
   const [recipients, setRecipients] = useState<Array<{ name: string; email: string }>>([]);
   const [currentRecipientName, setCurrentRecipientName] = useState('');
   const [currentRecipientEmail, setCurrentRecipientEmail] = useState('');
   const [shareMessage, setShareMessage] = useState('');
   const [shareSubject, setShareSubject] = useState('');
   const [shareLoading, setShareLoading] = useState(false);
   const [toastOpen, setToastOpen] = useState(false);
   const [toastMessage, setToastMessage] = useState('');
   const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');
   const [editingCollectionId, setEditingCollectionId] = useState<string | null>(null);
   const [editingTitle, setEditingTitle] = useState('');
   const [editTitleModalOpen, setEditTitleModalOpen] = useState(false);
   const [selectedCollectionForEdit, setSelectedCollectionForEdit] =
      useState<SharedCollection | null>(null);

   useEffect(() => {
      if (currentUser) {
         fetchCollections();
      }
   }, [currentUser]);

   const fetchCollections = async () => {
      try {
         setLoading(true);
         const userCollections = await SharedCollectionService.getUserSharedCollections(
            currentUser!.uid
         );
         console.log(
            '🔍 Fetched collections with recipients:',
            userCollections.map((c) => ({
               id: c.id,
               title: c.title,
               sharedWith: c.sharedWith.map((r) => ({
                  email: r.email,
                  displayName: r.displayName,
                  photoURL: r.photoURL,
               })),
            }))
         );
         setCollections(userCollections);
      } catch (error) {
         console.error('Error fetching shared collections:', error);
         setError('Failed to load shared collections');
      } finally {
         setLoading(false);
      }
   };

   const handleDeleteCollection = async (collectionId: string) => {
      try {
         await SharedCollectionService.deleteSharedCollection(collectionId, currentUser!.uid);
         setCollections((prev) => prev.filter((c) => c.id !== collectionId));
         handleCloseMenu();
      } catch (error) {
         console.error('Error deleting collection:', error);
         setError('Failed to delete collection');
      }
   };

   const handleViewAnalytics = async (collection: SharedCollection) => {
      try {
         // Calculate analytics from collection data directly
         const recipientStats = {
            total: collection.sharedWith.length,
            viewed: collection.sharedWith.filter((r) => r.status === 'viewed').length,
            pending: collection.sharedWith.filter((r) => r.status === 'unviewed').length,
         };

         // Get top viewers from sharedWith array
         const topViewers = collection.sharedWith
            .filter((r) => r.viewedAt && r.viewCount > 0)
            .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
            .slice(0, 5)
            .map((r) => ({
               email: r.email,
               displayName: r.displayName,
               photoURL: r.photoURL,
               viewedAt: r.viewedAt!,
               viewCount: r.viewCount || 0,
            }));

         // Set initial analytics data without likes
         const initialAnalyticsData: CollectionAnalytics = {
            viewCount: getCombinedViewCount(collection),
            trafficBySource: collection.trafficBySource || {},
            recipientStats,
            topViewers,
         };

         // Open modal immediately with basic data
         setAnalytics(initialAnalyticsData);
         setSelectedCollection(collection);
         setAnalyticsDialogOpen(true);
         setAnalyticsLoading(true);
         handleCloseMenu();

         // Fetch likes analytics in the background
         try {
            const likesAnalytics = await SharedCollectionService.getCollectionLikesAnalytics(
               collection.id
            );

            // Update analytics with likes data
            setAnalytics((prev) =>
               prev
                  ? {
                       ...prev,
                       likesAnalytics,
                       viewCount:
                          likesAnalytics.totalCombinedViews ?? getCombinedViewCount(collection),
                       trafficBySource:
                          likesAnalytics.trafficBySource ?? prev.trafficBySource ?? {},
                    }
                  : initialAnalyticsData
            );
         } catch (likesError) {
            console.warn('Could not fetch likes analytics:', likesError);
            // Continue without likes analytics if it fails
         } finally {
            setAnalyticsLoading(false);
         }
      } catch (error) {
         console.error('Error calculating analytics:', error);
         setError('Failed to load analytics');
         setAnalyticsLoading(false);
      }
   };

   const handleCopyShareLink = async (
      shareToken: string,
      options?: { skipCloseMenu?: boolean },
   ) => {
      const socialShareUrl = `${window.location.origin}/shared/collection/${shareToken}`;
      try {
         await navigator.clipboard.writeText(socialShareUrl);
         showToast('Link copied — paste in Instagram, WhatsApp, or Messages.', 'success');
      } catch (error) {
         console.error('Failed to copy link:', error);
         showToast('Failed to copy link to clipboard', 'error');
      }
      if (!options?.skipCloseMenu) {
         handleCloseMenu();
      }
   };

   const handleMenuClick = (event: React.MouseEvent<HTMLElement>, collection: SharedCollection) => {
      setMenuAnchorEl(event.currentTarget);
      setSelectedCollectionForMenu(collection);
   };

   const handleCloseMenu = () => {
      setMenuAnchorEl(null);
      setSelectedCollectionForMenu(null);
   };

   const showToast = (message: string, severity: 'success' | 'error') => {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
   };

   const handleToastClose = () => {
      setToastOpen(false);
   };

   // Utility function to truncate title to 20 characters
   const truncateTitle = (title: string, maxLength: number = 20) => {
      if (title.length <= maxLength) return title;
      return title.substring(0, maxLength) + '...';
   };

   const handleEditTitle = (collection: SharedCollection) => {
      setSelectedCollectionForEdit(collection);
      setEditingTitle(
         collection.title || collection.subject || `Photos from ${collection.entityTitle}`
      );
      setEditTitleModalOpen(true);
   };

   const handleSaveTitle = async () => {
      if (!selectedCollectionForEdit || !editingTitle.trim()) {
         showToast('Title cannot be empty', 'error');
         return;
      }

      try {
         // Update collection title in backend
         const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/shared-collections/${selectedCollectionForEdit.id}/title`,
            {
               method: 'PATCH',
               headers: {
                  'Content-Type': 'application/json',
                  ...(await getAuthHeaders()),
               },
               body: JSON.stringify({ title: editingTitle.trim() }),
            }
         );

         if (!response.ok) {
            throw new Error('Failed to update collection title');
         }

         // Update local state
         setCollections((prev) =>
            prev.map((col) =>
               col.id === selectedCollectionForEdit.id
                  ? { ...col, title: editingTitle.trim() }
                  : col
            )
         );

         handleCloseEditTitleModal();
         showToast('Collection title updated successfully', 'success');
      } catch (error) {
         console.error('Error updating collection title:', error);
         showToast('Failed to update collection title', 'error');
      }
   };

   const handleCloseEditTitleModal = () => {
      setEditTitleModalOpen(false);
      setSelectedCollectionForEdit(null);
      setEditingTitle('');
   };

   const handleCancelEdit = () => {
      setEditingCollectionId(null);
      setEditingTitle('');
   };

   const handleViewCollection = (collection: SharedCollection) => {
      // Navigate to view collection photos
      const shareUrl = `/shared/collection/${collection.shareToken}`;
      window.open(shareUrl, '_blank');
   };

   const handleShareCollection = (collection: SharedCollection) => {
      setCollectionToShare(collection);
      setShareSubject(`Photos from ${collection.entityTitle}`);
      setShareMessage(`Check out these photos from ${collection.entityTitle}!`);
      // Reset form
      setRecipients([]);
      setCurrentRecipientName('');
      setCurrentRecipientEmail('');
      setShareEmails('');
      setShareDialogOpen(true);
      handleCloseMenu(); // Close the dropdown menu
   };

   const addRecipient = () => {
      if (currentRecipientEmail.trim()) {
         const newRecipient = {
            name: currentRecipientName.trim() || currentRecipientEmail.split('@')[0],
            email: currentRecipientEmail.trim().toLowerCase(),
         };

         // Check if email already exists
         if (!recipients.find((r) => r.email === newRecipient.email)) {
            setRecipients([...recipients, newRecipient]);
         }

         setCurrentRecipientName('');
         setCurrentRecipientEmail('');
      }
   };

   const removeRecipient = (emailToRemove: string) => {
      setRecipients(recipients.filter((r) => r.email !== emailToRemove));
   };

   const handleKeyPress = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
         event.preventDefault();
         addRecipient();
      }
   };

   const handleShareSubmit = async () => {
      if (!collectionToShare || !shareEmails.trim()) {
         showToast('Please enter at least one email address', 'error');
         return;
      }

      setShareLoading(true);
      try {
         // Parse emails (split by comma or semicolon)
         const emailList = shareEmails
            .split(/[,;]/)
            .map((email) => email.trim())
            .filter((email) => email.length > 0);

         if (emailList.length === 0) {
            showToast('Please enter valid email addresses', 'error');
            setShareLoading(false);
            return;
         }

         // Use the share existing collection functionality
         const result = await SharedCollectionService.shareExistingCollection(
            collectionToShare.id,
            emailList,
            shareMessage || undefined,
            shareSubject || undefined,
            currentUser?.displayName || currentUser?.email || 'Someone'
         );

         if (result.success) {
            setShareDialogOpen(false);
            setShareEmails('');
            setShareMessage('');
            setShareSubject('');
            setCollectionToShare(null);

            // Show success toast
            showToast(
               `Successfully shared "${collectionToShare.title}" with ${emailList.length} recipient(s)!`,
               'success'
            );

            // Show additional info if some emails failed
            if (result.errors && result.errors.length > 0) {
               setTimeout(() => {
                  showToast(
                     `Note: ${result.errors!.length} email(s) failed to send. Check console for details.`,
                     'error'
                  );
               }, 2000);
            }

            // Refresh collections to show updated recipient data
            fetchCollections();
         } else {
            throw new Error(result.message || 'Failed to share collection');
         }
      } catch (error) {
         console.error('Error sharing collection:', error);
         showToast('Failed to share collection. Please try again.', 'error');
      } finally {
         setShareLoading(false);
      }
   };

   const getEntityTypeIcon = (entityType: string) => {
      switch (entityType) {
         case 'trip':
            return '✈️';
         case 'event':
            return '📅';
         case 'album':
            return '📸';
         default:
            return '📁';
      }
   };

   const getStatusColor = (status: string) => {
      switch (status) {
         case 'joined':
            return 'success';
         case 'viewed':
            return 'info';
         case 'pending':
            return 'warning';
         default:
            return 'default';
      }
   };

   if (loading) {
      return (
         <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
            <CircularProgress />
         </Box>
      );
   }

   if (error) {
      return (
         <Box p={3}>
            <Alert severity='error'>{error}</Alert>
         </Box>
      );
   }

   return (
      <Box p={3}>
         <Typography variant='h4' gutterBottom>
            📤 Shared Photo Collections
         </Typography>
         <Typography variant='body1' color='textSecondary' paragraph>
            View and manage the photo collections you've shared with others from your trips, events,
            and albums.
         </Typography>

         {collections.length === 0 ? (
            <Alert severity='info'>
               You haven't shared any photo collections yet. When you share photos from trips,
               events, or albums, they'll appear here for easy management.
            </Alert>
         ) : (
            <Grid container spacing={2}>
               {collections.map((collection) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={collection.id}>
                     <Card sx={{ boxShadow: 1 }}>
                        <CardMedia
                           component='div'
                           sx={{
                              height: 140,
                              background:
                                 collection.photos.length > 0
                                    ? `url(${collection.photos[0].thumbnailUrl}) center/cover`
                                    : 'linear-gradient(45deg, #f0f0f0, #e0e0e0)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                           }}
                        >
                           {collection.photos.length === 0 && (
                              <PhotoLibraryIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                           )}
                           <Box
                              position='absolute'
                              top={8}
                              right={8}
                              bgcolor='rgba(0,0,0,0.7)'
                              borderRadius={1}
                              px={1}
                              py={0.5}
                           >
                              <Typography variant='caption' color='white'>
                                 {collection.photos.length} photos
                              </Typography>
                           </Box>
                        </CardMedia>

                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                           <Box display='flex' alignItems='center' gap={1} mb={1}>
                              <Typography variant='body2' sx={{ fontSize: '0.875rem' }}>
                                 {getEntityTypeIcon(collection.entityType)}
                              </Typography>
                              <Box display='flex' alignItems='center' gap={1} sx={{ flex: 1 }}>
                                 <Tooltip
                                    title={
                                       collection.title ||
                                       collection.subject ||
                                       `Photos from ${collection.entityTitle}`
                                    }
                                    placement='top'
                                 >
                                    <Typography
                                       variant='subtitle2'
                                       noWrap
                                       sx={{ fontWeight: 500, flex: 1 }}
                                    >
                                       {truncateTitle(
                                          collection.title ||
                                             collection.subject ||
                                             `Photos from ${collection.entityTitle}`
                                       )}
                                    </Typography>
                                 </Tooltip>
                                 <Tooltip title='Edit collection title'>
                                    <IconButton
                                       size='small'
                                       onClick={() => handleEditTitle(collection)}
                                       sx={{
                                          '&:hover': {
                                             backgroundColor: 'primary.main',
                                             color: 'white',
                                          },
                                       }}
                                    >
                                       <EditIcon fontSize='small' />
                                    </IconButton>
                                 </Tooltip>
                              </Box>
                           </Box>

                           <Typography
                              variant='caption'
                              color='textSecondary'
                              display='block'
                              gutterBottom
                           >
                              From: {collection.entityTitle}
                           </Typography>

                           <Typography
                              variant='caption'
                              color='textSecondary'
                              display='block'
                              gutterBottom
                           >
                              Shared:{' '}
                              {collection.createdAt &&
                              !isNaN(new Date(collection.createdAt).getTime())
                                 ? format(new Date(collection.createdAt), 'MMM dd, yyyy')
                                 : 'Unknown date'}
                           </Typography>

                           <Box
                              display='flex'
                              alignItems='center'
                              justifyContent='space-between'
                              gap={1}
                              flexWrap='wrap'
                              mt={1}
                           >
                              <Box display='flex' alignItems='center' gap={0.5}>
                                 <VisibilityIcon fontSize='small' color='action' />
                                 <Typography variant='caption' color='textSecondary'>
                                    {getCombinedViewCount(collection)} views
                                 </Typography>
                              </Box>
                              {collection.shareToken && (
                                 <Tooltip title='Copy social link (Instagram, WhatsApp, SMS)'>
                                    <Button
                                       size='small'
                                       variant='outlined'
                                       color='primary'
                                       startIcon={<ShareIcon sx={{ fontSize: 18 }} />}
                                       onClick={() =>
                                          void handleCopyShareLink(collection.shareToken!, {
                                             skipCloseMenu: true,
                                          })
                                       }
                                       sx={{
                                          textTransform: 'none',
                                          fontSize: '0.75rem',
                                          py: 0.25,
                                          px: 1,
                                          minHeight: 28,
                                       }}
                                    >
                                       Copy link
                                    </Button>
                                 </Tooltip>
                              )}
                           </Box>

                           <Box
                              mt={1}
                              display='flex'
                              alignItems='center'
                              justifyContent='space-between'
                           >
                              <AvatarGroup
                                 max={4}
                                 sx={{
                                    justifyContent: 'flex-start',
                                    '& .MuiAvatar-root': {
                                       width: 20,
                                       height: 20,
                                       fontSize: '0.7rem',
                                    },
                                 }}
                              >
                                 {collection.sharedWith.map((recipient, index) => (
                                    <Tooltip
                                       key={index}
                                       title={`${recipient.displayName || recipient.email} (${recipient.status})`}
                                    >
                                       <Avatar
                                          src={recipient.photoURL || undefined}
                                          sx={{ width: 20, height: 20, fontSize: '0.7rem' }}
                                       >
                                          {!recipient.photoURL &&
                                             (recipient.displayName ||
                                                recipient.email ||
                                                'U')[0].toUpperCase()}
                                       </Avatar>
                                    </Tooltip>
                                 ))}
                              </AvatarGroup>

                              <IconButton
                                 size='small'
                                 onClick={(e) => handleMenuClick(e, collection)}
                              >
                                 <MoreVertIcon fontSize='small' />
                              </IconButton>
                           </Box>
                        </CardContent>
                     </Card>
                  </Grid>
               ))}
            </Grid>
         )}

         {/* Menu */}
         <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
            <MenuItem
               onClick={() =>
                  selectedCollectionForMenu && handleViewCollection(selectedCollectionForMenu)
               }
            >
               <ViewModuleIcon fontSize='small' sx={{ mr: 1 }} />
               View Collection
            </MenuItem>
            <MenuItem
               onClick={() =>
                  selectedCollectionForMenu && handleShareCollection(selectedCollectionForMenu)
               }
            >
               <PersonAddIcon fontSize='small' sx={{ mr: 1 }} />
               Share with Others
            </MenuItem>
            <MenuItem
               onClick={() =>
                  selectedCollectionForMenu &&
                  handleCopyShareLink(selectedCollectionForMenu.shareToken!)
               }
            >
               <LinkIcon fontSize='small' sx={{ mr: 1 }} />
               Public Link
            </MenuItem>
            <MenuItem
               onClick={() =>
                  selectedCollectionForMenu && handleViewAnalytics(selectedCollectionForMenu)
               }
            >
               <AnalyticsIcon fontSize='small' sx={{ mr: 1 }} />
               Personal Send Analytics
            </MenuItem>
            <MenuItem
               onClick={() =>
                  selectedCollectionForMenu && handleDeleteCollection(selectedCollectionForMenu.id)
               }
               sx={{ color: 'error.main' }}
            >
               <DeleteIcon fontSize='small' sx={{ mr: 1 }} />
               Delete Collection
            </MenuItem>
         </Menu>

         {/* Analytics Dialog */}
         <Dialog
            open={analyticsDialogOpen}
            onClose={() => {
               setAnalyticsDialogOpen(false);
               setAnalyticsLoading(false);
            }}
            maxWidth='lg'
            fullWidth
         >
            <DialogTitle>📊 Analytics - {selectedCollection?.title}</DialogTitle>
            <DialogContent>
               {analytics && (
                  <Box>
                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           <Card>
                              <CardContent>
                                 <Typography variant='h6' gutterBottom>
                                    View Statistics
                                 </Typography>
                                 <Typography variant='h3' color='primary'>
                                    {analytics.viewCount}
                                 </Typography>
                                 <Typography variant='body2' color='textSecondary'>
                                    Total views
                                 </Typography>
                                 <Typography variant='caption' color='textSecondary' display='block' sx={{ mt: 1 }}>
                                    Email-recipient first opens plus tracked link sessions (see
                                    traffic sources).
                                 </Typography>
                              </CardContent>
                           </Card>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                           <Card>
                              <CardContent>
                                 <Typography variant='h6' gutterBottom>
                                    Recipient Status
                                 </Typography>
                                 <Box display='flex' gap={1} flexWrap='wrap'>
                                    <Chip
                                       label={`${analytics.recipientStats.viewed} Viewed`}
                                       color='success'
                                       size='small'
                                    />
                                    <Chip
                                       label={`${analytics.recipientStats.pending} Unviewed`}
                                       color='warning'
                                       size='small'
                                    />
                                 </Box>
                              </CardContent>
                           </Card>
                        </Grid>

                        <Grid item xs={12}>
                           <Card>
                              <CardContent>
                                 <Typography variant='h6' gutterBottom>
                                    Traffic sources
                                 </Typography>
                                 <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
                                    Opens attributed to tracked share links (UTM). Use per-app copy
                                    in the share dialog; plain links count as direct.
                                 </Typography>
                                 {Object.keys(analytics.trafficBySource || {}).length === 0 ? (
                                    <Typography variant='body2' color='textSecondary'>
                                       No attributed opens yet. Share using the tracked links from
                                       the photo share flow.
                                    </Typography>
                                 ) : (
                                    <List dense disablePadding>
                                       {Object.entries(analytics.trafficBySource || {})
                                          .sort(([, a], [, b]) => b - a)
                                          .map(([source, count]) => (
                                             <ListItem key={source} disableGutters sx={{ py: 0.5 }}>
                                                <ListItemText
                                                   primary={formatTrafficSourceLabel(source)}
                                                   secondary={`${count} open${count === 1 ? '' : 's'}`}
                                                />
                                             </ListItem>
                                          ))}
                                    </List>
                                 )}
                              </CardContent>
                           </Card>
                        </Grid>
                     </Grid>

                     {/* Likes Analytics Section */}
                     {analytics.likesAnalytics && (
                        <Box mt={3}>
                           <Typography variant='h6' gutterBottom>
                              Likes Analytics
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12} sm={4}>
                                 <Card>
                                    <CardContent>
                                       <Box display='flex' alignItems='center' gap={1}>
                                          <FavoriteIcon color='error' />
                                          <Typography variant='h6' gutterBottom>
                                             Total Likes
                                          </Typography>
                                       </Box>
                                       <Typography variant='h3' color='primary'>
                                          {analytics.likesAnalytics.totalLikes}
                                       </Typography>
                                       <Typography variant='body2' color='textSecondary'>
                                          Across all photos
                                       </Typography>
                                    </CardContent>
                                 </Card>
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                 <Card>
                                    <CardContent>
                                       <Box display='flex' alignItems='center' gap={1}>
                                          <PhotoLibraryIcon color='primary' />
                                          <Typography variant='h6' gutterBottom>
                                             Photos
                                          </Typography>
                                       </Box>
                                       <Typography variant='h3' color='primary'>
                                          {analytics.likesAnalytics.photoCount}
                                       </Typography>
                                       <Typography variant='body2' color='textSecondary'>
                                          Total photos
                                       </Typography>
                                    </CardContent>
                                 </Card>
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                 <Card>
                                    <CardContent>
                                       <Box display='flex' alignItems='center' gap={1}>
                                          <TrendingUpIcon color='success' />
                                          <Typography variant='h6' gutterBottom>
                                             Average
                                          </Typography>
                                       </Box>
                                       <Typography variant='h3' color='primary'>
                                          {analytics.likesAnalytics.averageLikesPerPhoto}
                                       </Typography>
                                       <Typography variant='body2' color='textSecondary'>
                                          Likes per photo
                                       </Typography>
                                    </CardContent>
                                 </Card>
                              </Grid>
                           </Grid>

                           {/* Most Liked Photos */}
                           <Box mt={3}>
                              <Typography variant='h6' gutterBottom>
                                 Most Liked Photos
                              </Typography>
                              {analyticsLoading ? (
                                 <Box
                                    display='flex'
                                    justifyContent='center'
                                    alignItems='center'
                                    minHeight={120}
                                 >
                                    <CircularProgress />
                                    <Typography variant='body2' color='textSecondary' ml={2}>
                                       Loading photo likes...
                                    </Typography>
                                 </Box>
                              ) : analytics.likesAnalytics?.mostLikedPhotos.length > 0 ? (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       overflowX: 'auto',
                                       gap: 1,
                                       pb: 1,
                                       '&::-webkit-scrollbar': {
                                          height: 8,
                                       },
                                       '&::-webkit-scrollbar-track': {
                                          backgroundColor: '#f1f1f1',
                                          borderRadius: 10,
                                       },
                                       '&::-webkit-scrollbar-thumb': {
                                          backgroundColor: '#c1c1c1',
                                          borderRadius: 10,
                                       },
                                    }}
                                 >
                                    {analytics.likesAnalytics.mostLikedPhotos.map(
                                       (photo, index) => (
                                          <Card
                                             key={photo.id}
                                             sx={{ minWidth: 120, maxWidth: 120, flexShrink: 0 }}
                                          >
                                             <CardMedia
                                                component='img'
                                                height='80'
                                                image={photo.thumbnailUrl || photo.url}
                                                alt={photo.title}
                                                sx={{
                                                   objectFit: 'cover',
                                                   width: '80px',
                                                   height: '80px',
                                                }}
                                             />
                                             <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                                <Box
                                                   display='flex'
                                                   alignItems='center'
                                                   justifyContent='center'
                                                   gap={0.5}
                                                >
                                                   <FavoriteIcon color='error' fontSize='small' />
                                                   <Typography
                                                      variant='caption'
                                                      color='primary'
                                                      fontWeight='bold'
                                                   >
                                                      {photo.totalLikes}
                                                   </Typography>
                                                </Box>
                                             </CardContent>
                                          </Card>
                                       )
                                    )}
                                 </Box>
                              ) : (
                                 <Typography
                                    variant='body2'
                                    color='textSecondary'
                                    textAlign='center'
                                    py={2}
                                 >
                                    No liked photos yet
                                 </Typography>
                              )}
                           </Box>
                        </Box>
                     )}

                     {/* Detailed Recipients List */}
                     <Box mt={3}>
                        <Typography variant='h6' gutterBottom>
                           All Recipients
                        </Typography>
                        <List>
                           {selectedCollection?.sharedWith.map((recipient, index) => (
                              <ListItem key={index} divider>
                                 <ListItemAvatar>
                                    <Avatar src={recipient.photoURL || undefined}>
                                       {!recipient.photoURL &&
                                          (recipient.displayName ||
                                             recipient.email ||
                                             'U')[0].toUpperCase()}
                                    </Avatar>
                                 </ListItemAvatar>
                                 <ListItemText
                                    primary={
                                       <Box display='flex' alignItems='center' gap={1}>
                                          <Typography variant='body1'>
                                             {recipient.displayName || recipient.email}
                                          </Typography>
                                          <Chip
                                             label={recipient.status}
                                             size='small'
                                             color={
                                                recipient.status === 'viewed'
                                                   ? 'success'
                                                   : 'warning'
                                             }
                                          />
                                       </Box>
                                    }
                                    secondary={
                                       <Box>
                                          <Typography variant='body2' color='textSecondary'>
                                             Email: {recipient.email}
                                          </Typography>
                                          <Typography variant='body2' color='textSecondary'>
                                             Sent:{' '}
                                             {(() => {
                                                // Check both dateSent (new) and invitedAt (legacy) fields
                                                const sentDate =
                                                   recipient.dateSent ||
                                                   (recipient as any).invitedAt;
                                                return sentDate &&
                                                   !isNaN(new Date(sentDate).getTime())
                                                   ? format(
                                                        new Date(sentDate),
                                                        'MMM dd, yyyy HH:mm'
                                                     )
                                                   : 'Unknown date';
                                             })()}
                                          </Typography>
                                          {recipient.viewedAt && (
                                             <Typography variant='body2' color='textSecondary'>
                                                First viewed:{' '}
                                                {format(
                                                   new Date(recipient.viewedAt),
                                                   'MMM dd, yyyy HH:mm'
                                                )}
                                             </Typography>
                                          )}
                                          <Typography variant='body2' color='textSecondary'>
                                             View count: {recipient.viewCount || 0}
                                          </Typography>
                                       </Box>
                                    }
                                 />
                              </ListItem>
                           ))}
                        </List>
                     </Box>

                     {analytics.topViewers.length > 0 && (
                        <Box mt={3}>
                           <Typography variant='h6' gutterBottom>
                              Top Viewers
                           </Typography>
                           <List>
                              {analytics.topViewers.map((viewer, index) => (
                                 <ListItem key={index}>
                                    <ListItemAvatar>
                                       <Avatar src={viewer.photoURL || undefined}>
                                          {!viewer.photoURL &&
                                             (viewer.displayName ||
                                                viewer.email ||
                                                'U')[0].toUpperCase()}
                                       </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                       primary={viewer.displayName || viewer.email}
                                       secondary={`Viewed on ${
                                          viewer.viewedAt &&
                                          !isNaN(new Date(viewer.viewedAt).getTime())
                                             ? format(
                                                  new Date(viewer.viewedAt),
                                                  'MMM dd, yyyy HH:mm'
                                               )
                                             : 'Unknown date'
                                       }`}
                                    />
                                 </ListItem>
                              ))}
                           </List>
                        </Box>
                     )}
                  </Box>
               )}
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={() => {
                     setAnalyticsDialogOpen(false);
                     setAnalyticsLoading(false);
                  }}
               >
                  Close
               </Button>
            </DialogActions>
         </Dialog>

         {/* Share Dialog */}
         <Dialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            maxWidth='md'
            fullWidth
         >
            <DialogTitle>📤 Share Collection: {collectionToShare?.title}</DialogTitle>
            <DialogContent>
               <Box mt={2}>
                  <Typography variant='body2' color='textSecondary' paragraph>
                     Share this collection with new recipients. They will receive an email with the
                     photos and be added to your network.
                  </Typography>

                  <TextField
                     fullWidth
                     label='Email Addresses'
                     placeholder='Enter email addresses separated by commas'
                     value={shareEmails}
                     onChange={(e) => setShareEmails(e.target.value)}
                     helperText='Separate multiple email addresses with commas'
                     sx={{ mb: 2 }}
                  />

                  <TextField
                     fullWidth
                     label='Subject'
                     value={shareSubject}
                     onChange={(e) => setShareSubject(e.target.value)}
                     sx={{ mb: 2 }}
                  />

                  <TextField
                     fullWidth
                     label='Personal Message (Optional)'
                     multiline
                     rows={3}
                     value={shareMessage}
                     onChange={(e) => setShareMessage(e.target.value)}
                     placeholder='Add a personal message to your recipients...'
                  />

                  {collectionToShare && (
                     <>
                        <Box mt={3}>
                           <Divider sx={{ mb: 2 }} />
                           <Typography variant='h6' gutterBottom>
                              Collection Preview
                           </Typography>
                           <Box display='flex' alignItems='center' gap={2}>
                              <Box
                                 sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 1,
                                    background:
                                       collectionToShare.photos.length > 0
                                          ? `url(${collectionToShare.photos[0].thumbnailUrl}) center/cover`
                                          : 'linear-gradient(45deg, #f0f0f0, #e0e0e0)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                 }}
                              >
                                 {collectionToShare.photos.length === 0 && (
                                    <PhotoLibraryIcon sx={{ color: 'grey.400' }} />
                                 )}
                              </Box>
                              <Box>
                                 <Typography variant='subtitle2'>
                                    {collectionToShare.photos.length} photos
                                 </Typography>
                                 <Typography variant='body2' color='textSecondary'>
                                    From: {collectionToShare.entityTitle}
                                 </Typography>
                                 <Typography variant='body2' color='textSecondary'>
                                    Layout: {collectionToShare.layout}
                                 </Typography>
                              </Box>
                           </Box>
                        </Box>

                        {/* Existing Recipients */}
                        {collectionToShare.sharedWith.length > 0 && (
                           <Box mt={3}>
                              <Divider sx={{ mb: 2 }} />
                              <Typography variant='h6' gutterBottom>
                                 Current Recipients ({collectionToShare.sharedWith.length})
                              </Typography>
                              <Typography variant='body2' color='textSecondary' paragraph>
                                 Adding emails that already received this collection will resend it
                                 to them.
                              </Typography>
                              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                 <List dense>
                                    {collectionToShare.sharedWith.map((recipient, index) => (
                                       <ListItem key={index} sx={{ py: 0.5 }}>
                                          <ListItemAvatar>
                                             <Avatar
                                                src={recipient.photoURL || undefined}
                                                sx={{ width: 32, height: 32 }}
                                             >
                                                {!recipient.photoURL &&
                                                   (recipient.displayName ||
                                                      recipient.email ||
                                                      'U')[0].toUpperCase()}
                                             </Avatar>
                                          </ListItemAvatar>
                                          <ListItemText
                                             primary={
                                                <Box display='flex' alignItems='center' gap={1}>
                                                   <Typography variant='body2'>
                                                      {recipient.displayName || recipient.email}
                                                   </Typography>
                                                   <Chip
                                                      label={recipient.status}
                                                      size='small'
                                                      color={
                                                         recipient.status === 'viewed'
                                                            ? 'success'
                                                            : 'warning'
                                                      }
                                                   />
                                                </Box>
                                             }
                                             secondary={
                                                <Typography variant='caption' color='textSecondary'>
                                                   {recipient.email} • Views:{' '}
                                                   {recipient.viewCount || 0}
                                                </Typography>
                                             }
                                          />
                                       </ListItem>
                                    ))}
                                 </List>
                              </Box>
                           </Box>
                        )}
                     </>
                  )}
               </Box>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
               <Button
                  onClick={handleShareSubmit}
                  variant='contained'
                  color='primary'
                  disabled={!shareEmails.trim() || shareLoading}
                  startIcon={
                     shareLoading ? <CircularProgress size={16} color='inherit' /> : undefined
                  }
               >
                  {shareLoading ? 'Sharing...' : 'Share Collection'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Edit Title Modal */}
         <Dialog
            open={editTitleModalOpen}
            onClose={handleCloseEditTitleModal}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box display='flex' alignItems='center' gap={1}>
                  <EditIcon />
                  Edit Collection Title
               </Box>
            </DialogTitle>
            <DialogContent>
               <TextField
                  autoFocus
                  margin='dense'
                  label='Collection Title'
                  type='text'
                  fullWidth
                  variant='outlined'
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onKeyPress={(e) => {
                     if (e.key === 'Enter') {
                        handleSaveTitle();
                     }
                  }}
                  helperText='Enter a descriptive title for your photo collection'
                  sx={{ mt: 1 }}
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseEditTitleModal} color='inherit'>
                  Cancel
               </Button>
               <Button
                  onClick={handleSaveTitle}
                  variant='contained'
                  color='primary'
                  disabled={!editingTitle.trim()}
                  startIcon={<SaveIcon />}
               >
                  Save Title
               </Button>
            </DialogActions>
         </Dialog>

         {/* Toast Notification */}
         <Snackbar
            open={toastOpen}
            autoHideDuration={6000}
            onClose={handleToastClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={handleToastClose}
               severity={toastSeverity}
               sx={{ width: '100%' }}
               variant='filled'
            >
               {toastMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default SharedCollections;
